export type AuthTransportMode = 'session' | 'public';
export type AuthTransportExpectationMode = AuthTransportMode | 'blocked';

export type SessionValidationReason =
  | 'protected_request_unauthorized'
  | 'protected_request_forbidden'
  | 'authorization_changed'
  | 'missing_bearer_token'
  | 'focus'
  | 'visibility'
  | 'online'
  | 'interval'
  | 'auth_state_change'
  | 'manual_retry';

export type SessionFailureSignal = {
  reason: SessionValidationReason;
  source?: 'api' | 'supabase' | 'query' | 'mutation' | 'coordinator';
  status?: number;
};

export type LogoutRequest = {
  reason: 'manual_logout' | 'session_expired';
  preservePublicRoute?: boolean;
};

type SessionFailureHandler = (signal: SessionFailureSignal) => void | Promise<void>;
type LogoutHandler = (request: LogoutRequest) => void | Promise<void>;

let sessionFailureHandler: SessionFailureHandler | null = null;
let logoutHandler: LogoutHandler | null = null;

let transportExpectation: {
  mode: AuthTransportExpectationMode;
  accessToken: string | null;
} = {
  mode: typeof window !== 'undefined' && isAnonymousTransportPath(window.location.pathname)
    ? 'public'
    : 'session',
  accessToken: null,
};

export function registerSessionFailureHandler(handler: SessionFailureHandler): () => void {
  sessionFailureHandler = handler;
  return () => {
    if (sessionFailureHandler === handler) sessionFailureHandler = null;
  };
}

export function reportSessionFailure(signal: SessionFailureSignal): void {
  try {
    void sessionFailureHandler?.(signal);
  } catch {
    // Reporting must never replace the original request failure.
  }
}

export function registerLogoutHandler(handler: LogoutHandler): () => void {
  logoutHandler = handler;
  return () => {
    if (logoutHandler === handler) logoutHandler = null;
  };
}

export async function requestPrincipalLogout(request: LogoutRequest): Promise<void> {
  if (!logoutHandler) return;
  await logoutHandler(request);
}

export function setAuthTransportExpectation(
  mode: AuthTransportExpectationMode,
  accessToken: string | null,
): void {
  transportExpectation = { mode, accessToken };
}

export function getAuthTransportExpectation(): Readonly<typeof transportExpectation> {
  if (transportExpectation.mode === 'blocked' || typeof window === 'undefined') {
    return transportExpectation;
  }
  // Derive the route policy at request time. A passive React effect is too
  // late during public/protected SPA navigation because child effects may
  // issue requests before the coordinator effect commits.
  return {
    ...transportExpectation,
    mode: isAnonymousTransportPath(window.location.pathname) ? 'public' : 'session',
  };
}
import { isAnonymousTransportPath } from './routePolicy';
